<script setup>
import { ref, computed, watch, toRef } from 'vue'
import moment from 'moment-timezone'
import { useInstantBookingSlotsStore } from '@/stores'
import { storeToRefs } from 'pinia'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import { VuetifyTiptap } from 'vuetify-pro-tiptap'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  restaurant: {
    type: Object,
    required: true
  },
  slotData: {
    type: Object,
    default: null
  },
  cloneMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'saved', 'deleted'])

const instantBookingSlotsStore = useInstantBookingSlotsStore()
const { loading } = storeToRefs(instantBookingSlotsStore)

// Dialog visibility
const isDialogVisible = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val)
})

// Edit mode detection - clone mode is NOT edit mode (creates new slot)
const isEditMode = computed(() => !!props.slotData?._id && !props.cloneMode)
const isCloneMode = computed(() => props.cloneMode && !!props.slotData?._id)
const isViewingBookedSlot = computed(() => props.slotData?.status === 'booked' && !props.cloneMode)

// TipTap editor configuration
const tiptapOutput = ref('html')
const tiptapOutlined = ref(true)
const tiptapMaxWidth = ref(900)

// Form state
const form = ref({
  experienceName: 'Platinum Access',
  experienceDescription: 'This reservation is secured by a $xxxpp Food & Beverage minimum',
  date: moment().add(1, 'week').format('YYYY-MM-DD'),
  time: '19:30',
  partySize: 2,
  seatingArea: '',
  guaranteedSpendPerPerson: 200,
  taxPercentage: 12,
  tipPercentage: 20,
  ccFeePercentage: 0,
  paymentCaptureType: 'hold',
  cancellationPeriodHours: 24,
  penalty: 'percentage-50',
  expirationTime: null,
  expirationTimeManuallySet: false,
})

const formError = ref(null)
const formSuccess = ref(null)
const saving = ref(false)

// Delete confirmation dialog
const showDeleteDialog = ref(false)
const deleting = ref(false)

// Watch for slot changes (edit mode) and populate form
watch(() => props.slotData, newSlot => {
  if (newSlot && newSlot._id) {
    const penaltyKey = penaltyToKey(newSlot.penalty)
    const tz = newSlot.restaurantTimeZone || props.restaurant?.timeZone || 'America/Chicago'

    form.value = {
      experienceName: newSlot.experienceName || 'Platinum Access',
      experienceDescription: newSlot.experienceDescription || 'This reservation is secured by a $xxxpp Food & Beverage minimum',
      date: newSlot.date ? moment.tz(newSlot.date, tz).format('YYYY-MM-DD') : null,
      time: newSlot.time || '',
      partySize: newSlot.partySize || 2,
      seatingArea: newSlot.seatingArea || '',
      guaranteedSpendPerPerson: newSlot.guaranteedSpendPerPerson || 200,
      taxPercentage: newSlot.taxPercentage ?? 12,
      tipPercentage: newSlot.tipPercentage ?? 20,
      ccFeePercentage: newSlot.ccFeePercentage ?? 0,
      paymentCaptureType: newSlot.paymentCaptureType || 'hold',
      cancellationPeriodHours: newSlot.cancellationPeriodHours || 24,
      penalty: penaltyKey,
      expirationTime: newSlot.expirationTime ? moment.tz(newSlot.expirationTime, tz).format('YYYY-MM-DDTHH:mm') : null,
      expirationTimeManuallySet: false,
    }
  }
}, { immediate: true })

// Reset form when dialog opens for new slot
watch(isDialogVisible, visible => {
  if (visible && !props.slotData?._id) {
    resetForm()
  }
  if (!visible) {
    formError.value = null
    formSuccess.value = null
  }
})

const resetForm = () => {
  form.value = {
    experienceName: 'Platinum Access',
    experienceDescription: 'This reservation is secured by a $xxxpp Food & Beverage minimum',
    date: moment().add(1, 'week').format('YYYY-MM-DD'),
    time: '19:30',
    partySize: 2,
    seatingArea: '',
    guaranteedSpendPerPerson: 200,
    taxPercentage: 12,
    tipPercentage: 20,
    ccFeePercentage: 0,
    paymentCaptureType: 'hold',
    cancellationPeriodHours: 24,
    penalty: 'percentage-50',
    expirationTime: null,
    expirationTimeManuallySet: false,
  }
  formError.value = null
  formSuccess.value = null
}

// Helper functions for penalty conversion
const penaltyToKey = penalty => {
  if (!penalty || !penalty.type) return 'percentage-50'
  
  return `${penalty.type}-${penalty.amount}`
}

const keyToPenalty = key => {
  if (!key) return { type: 'percentage', amount: 50 }
  const [type, amountStr] = key.split('-')
  
  return { type, amount: Number(amountStr) }
}

// Calculate default expiration time
const calculateDefaultExpirationTime = () => {
  if (!form.value.date || !form.value.time || !form.value.cancellationPeriodHours) {
    return null
  }

  const dateTimeString = `${form.value.date} ${form.value.time}`
  const bookingDateTime = moment(dateTimeString, 'YYYY-MM-DD HH:mm')

  if (!bookingDateTime.isValid()) {
    return null
  }

  return bookingDateTime.subtract(form.value.cancellationPeriodHours, 'hours').format('YYYY-MM-DDTHH:mm')
}

// Watch for changes to date/time/cancellationPeriodHours and auto-update expirationTime
watch([() => form.value.date, () => form.value.time, () => form.value.cancellationPeriodHours], () => {
  if (!form.value.expirationTimeManuallySet) {
    const calculatedExpiration = calculateDefaultExpirationTime()
    if (calculatedExpiration) {
      form.value.expirationTime = calculatedExpiration
    }
  }
}, { immediate: true })

// Form validation and submission
const saveSlot = async () => {
  formError.value = null
  formSuccess.value = null

  // Validation
  if (!form.value.experienceName) {
    formError.value = 'Please enter an experience name'
    
    return
  }
  if (!form.value.date) {
    formError.value = 'Please select a date'
    
    return
  }
  if (!form.value.time) {
    formError.value = 'Please select a time'
    
    return
  }
  if (form.value.guaranteedSpendPerPerson <= 0) {
    formError.value = 'Guaranteed spend must be greater than 0'
    
    return
  }

  saving.value = true

  try {
    const slotData = {
      date: form.value.date,
      time: form.value.time,
      partySize: form.value.partySize,
      seatingArea: form.value.seatingArea,
      guaranteedSpendPerPerson: form.value.guaranteedSpendPerPerson,
      taxPercentage: form.value.taxPercentage,
      tipPercentage: form.value.tipPercentage,
      ccFeePercentage: form.value.ccFeePercentage,
      paymentCaptureType: form.value.paymentCaptureType,
      cancellationPeriodHours: form.value.cancellationPeriodHours,
      penalty: keyToPenalty(form.value.penalty),
      expirationTime: form.value.expirationTime,
      experienceName: form.value.experienceName,
      experienceDescription: form.value.experienceDescription,
    }

    if (isEditMode.value) {
      // Update existing slot
      await instantBookingSlotsStore.updateSlot(props.slotData._id, slotData)
      formSuccess.value = 'Slot updated successfully!'
    } else {
      // Create new slot (or clone)
      const restaurantId = props.restaurant._id || props.restaurant.id
      await instantBookingSlotsStore.createSlots({
        restaurantId,
        slots: [slotData],
        quantity: 1,
      })
      formSuccess.value = isCloneMode.value ? 'Slot cloned successfully!' : 'Slot created successfully!'
    }

    emit('saved')

    // Close dialog after short delay
    setTimeout(() => {
      isDialogVisible.value = false
    }, 1500)
  } catch (error) {
    console.error('Error saving slot:', error)
    formError.value = error.response?.data?.error || (isEditMode.value ? 'Failed to update slot' : 'Failed to create slot')
  } finally {
    saving.value = false
  }
}

const deleteSlot = async () => {
  deleting.value = true
  try {
    await instantBookingSlotsStore.deleteSlot(props.slotData._id)
    showDeleteDialog.value = false
    emit('deleted')
    isDialogVisible.value = false
  } catch (error) {
    console.error('Error deleting slot:', error)
    formError.value = error.response?.data?.error || 'Failed to delete slot'
  } finally {
    deleting.value = false
  }
}

// Time options (15-minute increments)
const timeOptions = computed(() => {
  const options = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = hour.toString().padStart(2, '0')
      const minuteStr = minute.toString().padStart(2, '0')
      const value = `${hourStr}:${minuteStr}`
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      const ampm = hour < 12 ? 'AM' : 'PM'
      const title = `${hour12}:${minuteStr} ${ampm}`
      options.push({ title, value })
    }
  }
  
  return options
})

// Party size options (1-20)
const partySizeOptions = computed(() => {
  const options = []
  for (let i = 1; i <= 20; i++) {
    options.push(i)
  }
  
  return options
})

// Spend per person options
const spendPerPersonOptions = computed(() => {
  const options = []
  for (let i = 25; i <= 1000; i += 25) {
    options.push({ title: `$${i}`, value: i })
  }
  
  return options
})

// Penalty options
const penaltyOptions = computed(() => {
  return [
    { title: 'No penalty (full refund)', value: 'percentage-0' },
    { title: '$10 per person', value: 'fixed-10' },
    { title: '$25 per person', value: 'fixed-25' },
    { title: '$50 per person', value: 'fixed-50' },
    { title: '$100 per person', value: 'fixed-100' },
    { title: '50% of guaranteed min pp', value: 'percentage-50' },
    { title: '100% of guaranteed min pp', value: 'percentage-100' },
  ]
})

// Tax percentage options
const taxPercentageOptions = computed(() => {
  const options = [0]
  for (let i = 1; i <= 20; i += 1) {
    options.push(i)
  }
  
  return options
})

// Tip percentage options
const tipPercentageOptions = computed(() => {
  const options = []
  for (let i = 20; i <= 50; i += 5) {
    options.push({ title: `${i}`, value: i })
  }
  
  return options
})

// CC Fee percentage options
const ccFeePercentageOptions = computed(() => {
  const options = []
  for (let i = 0; i <= 10; i++) {
    const value = i * 0.5
    options.push({ title: `${value}`, value: value })
  }
  
  return options
})
</script>

<template>
  <VDialog
    v-model="isDialogVisible"
    max-width="700"
    scrollable
  >
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between pa-4">
        <span>{{ isViewingBookedSlot ? 'View Booking' : (isCloneMode ? 'Clone Slot' : (isEditMode ? 'Edit Slot' : 'Add Instant Booking Slot')) }}</span>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="isDialogVisible = false"
        />
      </VCardTitle>

      <VDivider />

      <VCardText class="pa-4">
        <VForm @submit.prevent="saveSlot">
          <VAlert
            v-if="formError"
            type="error"
            class="mb-4"
            closable
            @click:close="formError = null"
          >
            {{ formError }}
          </VAlert>

          <VAlert
            v-if="formSuccess"
            type="success"
            class="mb-4"
            closable
            @click:close="formSuccess = null"
          >
            {{ formSuccess }}
          </VAlert>

          <!-- Restaurant (read-only) -->
          <VRow class="mb-2">
            <VCol cols="12">
              <VTextField
                :model-value="restaurant?.name"
                label="Restaurant"
                readonly
                disabled
                variant="outlined"
                density="compact"
              />
            </VCol>
          </VRow>

          <!-- Experience Details -->
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.experienceName"
                label="Experience Name *"
                placeholder="Platinum Access"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
            <VCol cols="12">
              <VuetifyTiptap
                v-model="form.experienceDescription"
                label="Experience Description"
                placeholder="Optional description"
                :output="tiptapOutput"
                :outlined="tiptapOutlined"
                :max-width="tiptapMaxWidth"
                :max-height="200"
                :disabled="isViewingBookedSlot"
                rounded
              />
            </VCol>
          </VRow>

          <!-- Seating Area -->
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.seatingArea"
                label="Seating Type (optional)"
                placeholder="Main Dining Room"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
          </VRow>

          <!-- Date, Time, Party Size -->
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <AppDateTimePicker
                v-model="form.date"
                label="Date *"
                :config="{ disableMobile: true, altInput: true, altFormat: 'D n/j', dateFormat: 'Y-m-d', maxDate: moment().add(1,'year').format('YYYY-MM-DD') }"
                :disabled="isViewingBookedSlot"
                placeholder="Select date"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.time"
                :items="timeOptions"
                label="Time *"
                :disabled="isViewingBookedSlot || !form.date"
                placeholder="Select time"
                variant="outlined"
                density="compact"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.partySize"
                :items="partySizeOptions"
                label="Party Size *"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
          </VRow>

          <!-- Pricing -->
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="form.guaranteedSpendPerPerson"
                :items="spendPerPersonOptions"
                label="Spend Per Person ($) *"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
          </VRow>

          <!-- Tax, Tip, CC Fee -->
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <VCombobox
                v-model.number="form.taxPercentage"
                :items="taxPercentageOptions"
                label="Tax %"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
                suffix="%"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model.number="form.tipPercentage"
                :items="tipPercentageOptions"
                label="Tip %"
                :disabled="isViewingBookedSlot"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                suffix="%"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model.number="form.ccFeePercentage"
                :items="ccFeePercentageOptions"
                label="CC Charge %"
                :disabled="isViewingBookedSlot"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                suffix="%"
              />
            </VCol>
          </VRow>

          <!-- Payment Type and Cancellation -->
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.paymentCaptureType"
                :items="[
                  { title: 'CC on hold', value: 'hold' },
                  { title: 'Upfront payment', value: 'immediate' },
                ]"
                label="Payment Type *"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model.number="form.cancellationPeriodHours"
                :items="[
                  { title: '6 hours', value: 6 },
                  { title: '12 hours', value: 12 },
                  { title: '24 hours', value: 24 },
                  { title: '48 hours', value: 48 },
                  { title: '1 week', value: 168 },
                ]"
                label="Cancellation Period *"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="form.penalty"
                :items="penaltyOptions"
                item-title="title"
                item-value="value"
                label="Cancellation Penalty *"
                :disabled="isViewingBookedSlot"
                variant="outlined"
                density="compact"
              />
            </VCol>
          </VRow>

          <!-- Expiration Time -->
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <AppDateTimePicker
                v-model="form.expirationTime"
                label="Expiration Date/Time *"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', altInput: true, altFormat: 'D n/j h:i K' }"
                :disabled="isViewingBookedSlot"
                @update:model-value="form.expirationTimeManuallySet = true"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
              class="d-flex align-center"
            >
              <VBtn
                v-if="form.expirationTimeManuallySet && !isViewingBookedSlot"
                icon="mdi-refresh"
                size="small"
                variant="text"
                title="Reset to auto-calculated time"
                @click="form.expirationTime = calculateDefaultExpirationTime(); form.expirationTimeManuallySet = false"
              />
              <span class="text-caption text-medium-emphasis">
                Auto-calculated from booking date minus cancellation period
              </span>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-4">
        <VBtn
          v-if="isEditMode && !isViewingBookedSlot"
          color="error"
          variant="outlined"
          @click="showDeleteDialog = true"
        >
          Delete
        </VBtn>
        <VSpacer />
        <VBtn
          variant="outlined"
          @click="isDialogVisible = false"
        >
          {{ isViewingBookedSlot ? 'Close' : 'Cancel' }}
        </VBtn>
        <VBtn
          v-if="!isViewingBookedSlot"
          color="primary"
          :loading="saving"
          :disabled="saving"
          @click="saveSlot"
        >
          {{ isEditMode ? 'Update Slot' : (isCloneMode ? 'Clone Slot' : 'Create Slot') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Delete Confirmation Dialog -->
  <VDialog
    v-model="showDeleteDialog"
    max-width="400"
  >
    <VCard>
      <VCardTitle>Confirm Delete</VCardTitle>
      <VCardText>
        Are you sure you want to delete this slot? This action cannot be undone.
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn @click="showDeleteDialog = false">
          Cancel
        </VBtn>
        <VBtn
          color="error"
          :loading="deleting"
          @click="deleteSlot"
        >
          Delete
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
